import { useUser } from "contexts/UserContext";
import moment from "moment-timezone";
import formatSessionDuration from "utils/formatSessionDuration";
import { getSessionTiming } from "utils/getSessionTiming";
import { PrintLayoutMetaData } from "./PrintLayout";

import styles from "./PrintHeader.module.scss";

interface PrintHeaderProps {
  metaData: PrintLayoutMetaData;
  clientName?: string;
}

const PrintHeader = ({ metaData, clientName }: PrintHeaderProps) => {
  const { defaultUserTimezone } = useUser();

  const { sessionStart, sessionEnd } = getSessionTiming(metaData.appointment);

  const sessionType = metaData.appointment?.appointment_type;

  const licenses = (metaData.orchid_pro?.licenses ?? []).map(
    ({ type, license_number }) => `${type ?? ""} #${license_number ?? ""}`,
  );

  const sessionStartMoment =
    !sessionStart && !metaData.group_session?.group_session_start
      ? null
      : moment.tz(
          sessionStart || metaData.group_session?.group_session_start,
          defaultUserTimezone,
        );

  const sessionEndMoment = moment.tz(
    sessionEnd || metaData.group_session?.group_session_end || "",
    defaultUserTimezone,
  );
  const sessionDuration = sessionEndMoment.diff(sessionStartMoment, "minutes");

  const sessionDateString = sessionStartMoment
    ? sessionStartMoment.format("dddd, MMM. D, YYYY")
    : "";

  const sessionTimeString = sessionStartMoment
    ? `${sessionStartMoment.format("h:mm")} - ${sessionEndMoment.format(
        "h:mm A",
      )} (${formatSessionDuration(sessionDuration)})`
    : "";

  return (
    <div className={styles["container"]}>
      <div className={styles["provider-info"]}>
        <div className={styles["title"]}>
          {metaData.orchid_pro?.first_name || ""}{" "}
          {metaData.orchid_pro?.last_name || ""}
        </div>
        <div className={styles["info-pane-section"]}>
          <div>{metaData.orchid_pro?.phone_number || ""}</div>
          <div>{metaData.orchid_pro?.email || ""}</div>
          {licenses.map((license) => (
            <div key={license}>{license}</div>
          ))}
        </div>
        <div className={styles["info-pane-section"]}>
          <div>{metaData.orchid_pro?.office_address_line1 || ""}</div>
          <div>{metaData.orchid_pro?.office_address_line2 || ""}</div>
          <div>
            {metaData.orchid_pro?.office_city
              ? `${metaData.orchid_pro?.office_city},`
              : ""}{" "}
            {metaData.orchid_pro?.office_state || ""}{" "}
            {metaData.orchid_pro?.office_zipcode || ""}
          </div>
        </div>
      </div>
      <div className={styles["client-info"]}>
        <div className={styles["info-pane-section"]}>
          <div className={styles["title"]}>Client Name</div>
          <div>{clientName || ""}</div>
        </div>
        <div className={styles["info-pane-section"]}>
          <div className={styles["title"]}>Client Contact</div>
          <div>{metaData.patient?.email || ""}</div>
          <div>{metaData.patient?.phone_number || ""}</div>
        </div>
      </div>
      <div className={styles["session-info"]}>
        <div className={styles["info-pane-section"]}>
          <div className={styles["title"]}>Session Type</div>
          <div>{sessionType}</div>
        </div>
        <div className={styles["info-pane-section"]}>
          <div className={styles["title"]}>Session Date</div>
          <div>{sessionDateString}</div>
          <div>{sessionTimeString}</div>
        </div>
      </div>
    </div>
  );
};

export default PrintHeader;
